export const TIME_RANGE_OPTIONS = {
  current: 'Current Month',
  last3: 'Last 3 Months',
  all: 'All Time'
} as const;

export type TimeRange = keyof typeof TIME_RANGE_OPTIONS;

export const STATUS_LABELS: Record<string, string> = {
  all: 'All Statuses',
  pending: 'Pending',
  pay_to_driver: 'Pay to Driver',
  PAID: 'Paid'
};

export interface InvoiceFilterState {
  /** Matches invoice number, order number, load number or destination */
  search: string;
  status: string;
  timeRange: TimeRange;
  broker: string;
  driver: string;
  /** Partial, case-insensitive match on the destination alone */
  destination: string;
  // "More filters"
  loadNumber: string;
  terminal: string;
  minAmount: string;
  maxAmount: string;
}

export const DEFAULT_FILTERS: InvoiceFilterState = {
  search: '',
  status: 'pending',
  timeRange: 'last3',
  broker: 'all',
  driver: 'all',
  destination: '',
  loadNumber: '',
  terminal: 'all',
  minAmount: '',
  maxAmount: ''
};

export type SortKey = 'createdAt' | 'invoiceNumber' | 'totalCharges';
export type SortDirection = 'asc' | 'desc';

export interface SortState {
  key: SortKey;
  direction: SortDirection;
}

export const SORT_OPTIONS: Array<{ label: string; key: SortKey; direction: SortDirection }> = [
  { label: 'Created (Newest)', key: 'createdAt', direction: 'desc' },
  { label: 'Created (Oldest)', key: 'createdAt', direction: 'asc' },
  { label: 'Invoice # (Low-High)', key: 'invoiceNumber', direction: 'asc' },
  { label: 'Invoice # (High-Low)', key: 'invoiceNumber', direction: 'desc' },
  { label: 'Amount (High-Low)', key: 'totalCharges', direction: 'desc' },
  { label: 'Amount (Low-High)', key: 'totalCharges', direction: 'asc' }
];

/** Columns the admin can hide from the gear menu. */
export const OPTIONAL_COLUMNS = {
  orderNumber: 'Order #',
  loadNumber: 'Load #',
  broker: 'Broker',
  terminal: 'Terminal',
  destination: 'Destination'
} as const;

export type OptionalColumn = keyof typeof OPTIONAL_COLUMNS;

export type ColumnVisibility = Record<OptionalColumn, boolean>;

export const DEFAULT_COLUMNS: ColumnVisibility = {
  orderNumber: true,
  loadNumber: true,
  broker: true,
  terminal: true,
  destination: true
};

export interface DriverInfo {
  dogtag: string;
  name: string;
  percentageCut: number;
}

export type DriverDirectory = Record<string, DriverInfo>;

/** Falls back to the dogtag when the driver has no user record. */
export const driverInfoFor = (directory: DriverDirectory, dogtag?: string): DriverInfo => {
  if (!dogtag) return { dogtag: '', name: 'Unassigned', percentageCut: 0 };
  return directory[dogtag] || { dogtag, name: dogtag, percentageCut: 0 };
};

/** The invoice shape this page reads and edits (Firestore documents are loose). */
export interface PendingInvoice {
  id: string;
  invoiceNumber?: string;
  createdAt?: any; // Firebase Timestamp
  updatedAt?: any; // Firebase Timestamp
  orderNumber?: string;
  loadNumber?: string;
  broker?: string;
  terminal?: string;
  destination?: string;
  driver?: string;
  totalCharges?: number;
  status?: 'pending' | 'pay_to_driver' | 'PAID';
  // Additional fields for editing and invoice generation
  gross?: number;
  rate?: number;
  net?: number;
  fuelSurchargePercent?: number;
  totalDem?: number;
  pumpEnabled?: boolean;
  pumpCount?: string;
  splitShareAmount?: number;
  freightRev?: number;
  fuelSurcharge?: number | string;
  demurrageCharge?: number;
  notes?: string;
  // Time fields
  date?: string;
  bolNumber?: string;
  terminalArrivalTime?: string;
  terminalDepartTime?: string;
  deliveryArrivalTime?: string;
  deliveryDepartTime?: string;
  // Invoice information fields
  invoiceDate?: string;
  dueDate?: string;
  miles?: string;
  customer?: string;
  address?: string;
  cityStateZip?: string;
  invoiceSheet?: string;
  rowNumber?: string;
}
